/**
 * Lightweight client feature flags.
 *
 * Whole View (docs/plans/2026-06-02-whole-view-plan.md) is flag-gated.
 * Resolution order (first match wins):
 *
 *   1. URL param (shareable, sticky):
 *        ?wholeView=1   // force on + persist to localStorage
 *        ?wholeView=0   // force off + clear
 *   2. localStorage:
 *        localStorage.setItem('cq.flag.wholeView', '1')  // force on
 *        localStorage.setItem('cq.flag.wholeView', '0')  // force off (BDD default)
 *   3. DEFAULT ON everywhere — preview/dev AND production. The PLAN (Whole View)
 *      tab now ships to all users by default. Still overridable per-client via
 *      ?wholeView=0 or localStorage('cq.flag.wholeView','0') (BDD relies on the
 *      localStorage opt-out).
 *
 * Net: on by default for everyone, overridable either way.
 * A later phase can promote this to a remote/org-level flag.
 */
const FLAG_KEY = 'cq.flag.wholeView';

export const isWholeViewEnabled = (): boolean => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    // 1. URL param wins and is sticky, so a shared link enables it for the session.
    const param = new URLSearchParams(window.location.search).get('wholeView');
    if (param === '1') {
      window.localStorage?.setItem(FLAG_KEY, '1');
      return true;
    }
    if (param === '0') {
      window.localStorage?.setItem(FLAG_KEY, '0');
      return false;
    }

    // 2. Explicit localStorage opt-in/out (sticky for shared links and BDD).
    const stored = window.localStorage?.getItem(FLAG_KEY);
    if (stored === '1') {
      return true;
    }
    if (stored === '0') {
      return false;
    }

    // 3. Default ON everywhere (preview/dev + production). Overridable above.
    return true;
  } catch {
    return false;
  }
};
